export const PV_STATUS_COLOR = {
  draft: 'gray',
  in_progress: 'amber',
  approved: 'blue',
  posted: 'green',
  rejected: 'red',
  returned: 'amber',
  cancelled: 'gray',
  reversed: 'purple',
};

export const PV_STATUS_LABEL = {
  draft: 'Draft',
  in_progress: 'IN PROGRESS',
  approved: 'Approved',
  posted: 'Posted',
  rejected: 'Rejected',
  returned: 'Returned',
  cancelled: 'Cancelled',
  reversed: 'Reversed',
};

export const pvStatusColor = (status) => PV_STATUS_COLOR[status] || 'gray';
export const pvStatusLabel = (status) => PV_STATUS_LABEL[status] || status;

// Every validation issue the backend reports comes back as one string like
// "This voucher could not be saved... (3 issues found):\n• Line 1 ...\n• ..."
// Split it into a header + bullet list so the UI can render it as a proper
// block instead of a run-on line.
export const parsePvErrorMessage = (message) => {
  if (!message) return { header: 'Something went wrong.', bullets: [] };
  const lines = String(message).split('\n').filter(Boolean);
  const bullets = lines.filter((l) => l.trim().startsWith('•')).map((l) => l.replace(/^•\s*/, ''));
  const header = lines.find((l) => !l.trim().startsWith('•')) || lines[0];
  return { header, bullets };
};

export const PAYMENT_TYPE_OPTIONS = [
  { value: 'supplier_payment', label: 'Supplier Payment' },
  // Staged-payment family — these describe *when* in the supply cycle the
  // money moves, so one approved requisition can be settled in tranches.
  { value: 'advance_payment', label: 'Advance Payment' },
  { value: 'mobilisation_advance', label: 'Mobilisation Advance' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'part_payment', label: 'Part Payment' },
  { value: 'progress_payment', label: 'Progress Payment' },
  { value: 'final_payment', label: 'Final Payment' },
  { value: 'retention_release', label: 'Retention Release' },
  { value: 'direct_expense', label: 'Direct Expense' },
  { value: 'reimbursement', label: 'Reimbursement' },
  { value: 'petty_cash', label: 'Petty Cash' },
  { value: 'imprest', label: 'Imprest' },
  { value: 'salary_related', label: 'Salary Related' },
  { value: 'statutory_payment', label: 'Statutory Payment' },
  { value: 'utility_payment', label: 'Utility Payment' },
  { value: 'asset_purchase', label: 'Asset Purchase' },
  { value: 'inventory_purchase', label: 'Inventory Purchase' },
  { value: 'project_payment', label: 'Project Payment' },
  { value: 'other', label: 'Other' },
];

export const CURRENCY_OPTIONS = [
  { value: 'NGN', label: 'NGN' },
  { value: 'USD', label: 'USD' },
  { value: 'GBP', label: 'GBP' },
  { value: 'EUR', label: 'EUR' },
];

export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// Every payment type that pays an external vendor against a supply contract
// and therefore requires a supplier. Mirrors SUPPLIER_REQUIRED_TYPES in
// backend/app/services/payment_voucher_service.py — keep the two in step.
export const SUPPLIER_REQUIRED_PAYMENT_TYPES = new Set([
  'supplier_payment', 'advance_payment', 'utility_payment', 'asset_purchase',
  'inventory_purchase', 'project_payment',
  'mobilisation_advance', 'deposit', 'part_payment', 'progress_payment',
  'final_payment', 'retention_release',
]);

// Payment status shown against a requisition in the Load Approved Requisition
// table. Derived server-side by _requisition_payment_status(); the frontend
// only colours it. Order here is the order shown in the status filter.
export const REQ_PAYMENT_STATUSES = [
  'Not Processed',
  'Partially Processed',
  'Part Paid',
  'Voucher Pending Approval',
  'Payment on Hold',
  'Payment Suspended',
];

export const REQ_PAYMENT_STATUS_COLORS = {
  'Not Processed': { bg: '#cbd5e1', color: '#334155', border: '#64748b' },
  'Partially Processed': { bg: '#fef3c7', color: '#b45309' },
  'Part Paid': { bg: '#dcfce7', color: '#15803d' },
  'Voucher Pending Approval': { bg: '#dbeafe', color: '#1d4ed8' },
  'Payment on Hold': { bg: '#ffedd5', color: '#c2410c', border: '#f97316' },
  'Payment Suspended': { bg: '#fee2e2', color: '#b91c1c', border: '#ef4444' },
};

// Badge colour for the DERIVED review status (see voucher_review_service.
// derive_review_status). Distinct from PV_STATUS_COLOR, which maps the raw
// workflow status — the register and the review screen both show the derived
// label, so they share this map.
export const REVIEW_STATUS_BADGE = {
  'Awaiting Review': 'amber',
  'Approved for Payment': 'blue',
  'On Hold': 'amber',
  'Returned': 'amber',
  'Rejected': 'red',
  'Released to Expense Register': 'green',
};

export const reviewStatusColor = (label) => REVIEW_STATUS_BADGE[label] || 'gray';

// Status badges carry long labels now ("Released to Expense Register"), so
// they wrap inside their cell instead of forcing the column wide and
// spilling over the one beside it.
export const STATUS_BADGE_STYLE = {
  textTransform: 'uppercase',
  fontSize: 10.5,
  padding: '3px 10px',
  whiteSpace: 'normal',
  display: 'inline-block',
  lineHeight: 1.35,
  maxWidth: 190,
};
